
/**
 * Interface for report configurations
 */
export interface ReportConfig {
  state: string;
  format: 'ASCII' | 'CSV' | 'JSON' | 'PDF';
  includeCourt?: boolean;
  includeCounty?: boolean;
  includeDriverLicense?: boolean;
  includeDateOfBirth?: boolean;
  customFields?: string[];
  submissionFrequency?: 'quarterly' | 'monthly' | 'annual';
  submissionDeadlineDays?: number; // Days after period end
  submissionMethod?: 'portal' | 'email' | 'mail' | 'ftp';
  submissionUrl?: string;
  helpText?: string;
  retentionYears?: number;
}

/**
 * State-specific reporting configurations
 */
export const STATE_REPORT_CONFIGS: Record<string, ReportConfig> = {
  'FL': {
    state: 'FL',
    format: 'ASCII',
    includeCourt: true,
    includeCounty: true,
    includeDriverLicense: true,
    includeDateOfBirth: true,
    submissionFrequency: 'quarterly',
    submissionDeadlineDays: 30, // 30 days after quarter end
    submissionMethod: 'portal',
    helpText: 'Reports must be submitted in ASCII II format to the FLHSMV within 30 days of quarter end.',
    retentionYears: 5
  },
  'TX': {
    state: 'TX',
    format: 'CSV',
    includeCourt: true,
    includeCounty: true,
    includeDriverLicense: true,
    includeDateOfBirth: false,
    submissionFrequency: 'monthly',
    submissionDeadlineDays: 15,
    submissionMethod: 'portal',
    helpText: 'Texas requires monthly reporting in CSV format within 15 days of month end.',
    retentionYears: 3
  },
  'CA': {
    state: 'CA',
    format: 'PDF',
    includeCourt: false,
    includeCounty: true,
    includeDriverLicense: true,
    includeDateOfBirth: true,
    submissionFrequency: 'quarterly',
    submissionDeadlineDays: 45,
    submissionMethod: 'email',
    helpText: 'California requires quarterly PDF reports submitted within 45 days of quarter end.',
    retentionYears: 3
  },
};

/**
 * Interface for certificate data in reports
 */
export interface CertificateReportData {
  certificate_number: string;
  student_name: string;
  driver_license?: string;
  dob?: string;
  completion_date: string;
  court_ordered?: boolean;
  county?: string;
  circuit?: string;
  custom_fields?: Record<string, any>;
}

/**
 * Report submission statuses
 */
export enum ReportStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected'
}

/**
 * Report deadline information
 */
export interface ReportDeadline {
  state: string;
  year: number;
  quarter?: number;
  month?: number;
  dueDate: Date;
  status: 'upcoming' | 'due' | 'overdue' | 'completed';
}

/**
 * Certificate report record interface
 * This interface must match the database schema for certificate_reports
 */
export interface CertificateReport {
  id?: string;
  year: number;
  quarter: number;
  state: string;
  format: string;
  report_data: string;
  generated_at?: string;
  generated_by?: string;
  status?: ReportStatus;
}

